import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Info component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconInfo extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.10547 12C1.10547 5.98284 5.98333 1.10498 12.0005 1.10498C18.0177 1.10498 22.8955 5.98284 22.8955 12C22.8955 18.0172 18.0177 22.895 12.0005 22.895C5.98333 22.895 1.10547 18.0172 1.10547 12ZM12.0005 2.60498C6.81176 2.60498 2.60547 6.81127 2.60547 12C2.60547 17.1888 6.81175 21.395 12.0005 21.395C17.1893 21.395 21.3955 17.1888 21.3955 12C21.3955 6.81126 17.1893 2.60498 12.0005 2.60498Z"
          />
          <path
            d="M12 8.1C12.4142 8.1 12.75 7.76421 12.75 7.35C12.75 6.93579 12.4142 6.6 12 6.6C11.5858 6.6 11.25 6.93579 11.25 7.35C11.25 7.76421 11.5858 8.1 12 8.1Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M10.5 7.35C10.5 6.52157 11.1716 5.85 12 5.85C12.8284 5.85 13.5 6.52157 13.5 7.35C13.5 8.17842 12.8284 8.85 12 8.85C11.1716 8.85 10.5 8.17842 10.5 7.35Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M10.4397 10.4702C10.0255 10.4702 9.6897 10.806 9.6897 11.2202C9.6897 11.6344 10.0255 11.9702 10.4397 11.9702H11.2505V15.9326H9.65918C9.24497 15.9326 8.90918 16.2684 8.90918 16.6826C8.90918 17.0968 9.24497 17.4326 9.65918 17.4326H11.9794C11.9864 17.4328 11.9934 17.4329 12.0005 17.4329C12.0075 17.4329 12.0145 17.4328 12.0215 17.4326H14.3415C14.7557 17.4326 15.0915 17.0968 15.0915 16.6826C15.0915 16.2684 14.7557 15.9326 14.3415 15.9326H12.7505V11.2202C12.7505 10.806 12.4147 10.4702 12.0005 10.4702H10.4397Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-info') === undefined) {
  customElements.define('cat-icon-info', BlocksIconInfo);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-info': BlocksIconInfo;
  }
}
